import React, { useEffect, useState } from 'react'

import { Loader } from '_components';
import { errorMessage, makeRequest, useRequest } from '_utils/request';

type DownloadProps<T> = {
    endpoint: string;
    onSuccess: (data: T) => void;
}

function Download<T>({ endpoint, onSuccess }: DownloadProps<T>) {
    const [result] = useRequest<T>(
        makeRequest(endpoint),
        response => response.status === 404 ? "404" : null
    );
  
    useEffect(() => {
        if (result.kind === "success") onSuccess(result.payload);
    });
  
    if (result.kind === "error") return <strong>{errorMessage(result.error)}</strong>
    else return <Loader />
}

type UploadProps<T> = {
    endpoint: string;
    data: T;
    onSuccess: () => void;
}

function Upload<T extends object>({ endpoint, data, onSuccess }: UploadProps<T>) {
    const request = makeRequest(endpoint, "PUT", data);
    const [result, fetchResult] = useRequest(request);
  
    useEffect(() => {
        if (result.kind === "success") onSuccess();
    });
    
    switch (result.kind) {
    case "error":
        return <>
            <p><strong>{errorMessage(result.error)}</strong></p>
            <button className="btn btn-primary" onClick={() => fetchResult(request)}>Try again</button>
        </>
    default:
        return <Loader />
    }
}

type EditProps<T> = {
    endpoint: string;
    Form: React.FC<{ data: T, onSubmit: (data: T) => void }>;
}

export function Edit<T extends object>({ endpoint, Form }: EditProps<T>) {
    type DownloadState = { kind: "download" };
    type EditState = { kind: "edit", data: T };
    type UploadState = { kind: "upload", data: T };
  
    type State = DownloadState | EditState | UploadState;
    const [state, setState] = useState<State>({ kind: "download" });
  
    switch (state.kind) {
        case "download":
            return <Download<T> endpoint={endpoint} onSuccess={(data) => setState({kind: "edit", data})} />
        case "edit":
            return <Form data={state.data} onSubmit={data => setState({ kind: "upload", data })}/>
        case "upload":
            return <Upload<T> endpoint={endpoint} data={state.data} onSuccess={() => setState({kind: "download"})} />
    }
}
